import type { ReactNode } from "react";

import type { DirectoryPreviewResponse, DirectoryResponse } from "@/apiClient";
import MarkdownEditor from "@/components/markdownEditor";
import SearchableSingleSelectDropdown from "@/components/dropdowns/searchableSingleSelectDropdown";
import Toggle from "@/components/toggle";

import PreviewExampleAccordion from "./PreviewExampleAccordion";
import type { PreviewDirectoryListItem } from "./folderConfig.utils";

type DirectoryUserOption = {
  value: string;
  label: string;
  searchText: string;
};

export type FolderConfigEditorPanelProps = {
  editingDirectory: DirectoryResponse | null;
  showForm: boolean;
  isDuplicateDocumentedPath: boolean;
  previewStatus: "already-documented" | "has-matches" | "no-matches" | "blank";
  previewErrorMessage: string | null;
  previewData: DirectoryPreviewResponse | undefined;
  isPreviewPending: boolean;
  templatePath: string;
  previewConcreteMatches: PreviewDirectoryListItem[];
  selectedPreviewMatch: PreviewDirectoryListItem | null;
  isPreviewExampleOpen: boolean;
  onTogglePreviewExampleOpen: () => void;
  onShowNextPreviewMatch: () => void;
  observedSubdirectories: FolderConfigEditorPanelPreviewData["observedSubdirectories"];
  observedFiles: FolderConfigEditorPanelPreviewData["observedFiles"];
  previewContentsLoading: boolean;
  previewContentsEmpty: boolean;
  builder: ReactNode;
  descriptionSeed: string;
  descriptionEditorResetKey: number;
  hasDescription: boolean;
  onDescriptionChange: (value: string) => void;
  allowAddendumUpload: boolean;
  onAllowAddendumUploadChange: (value: boolean) => void;
  selectedDataExpertUserId: string;
  selectedDataExpertLabel?: string;
  directoryUserOptions: DirectoryUserOption[];
  directoryDocumentersLoading: boolean;
  onSelectedDataExpertChange: (value: string) => void;
  onStartNew: () => void;
  canSubmit: boolean;
  saveButtonLabel: string;
  saveHelpText?: string;
  onSave: () => void;
};

type FolderConfigEditorPanelPreviewData = {
  observedSubdirectories: Parameters<
    typeof PreviewExampleAccordion
  >[0]["observedSubdirectories"];
  observedFiles: Parameters<typeof PreviewExampleAccordion>[0]["observedFiles"];
};

function PreviewStatusBanner({
  isDuplicateDocumentedPath,
  isPreviewPending,
  previewStatus,
  previewData,
  previewErrorMessage,
}: {
  isDuplicateDocumentedPath: boolean;
  isPreviewPending: boolean;
  previewStatus: FolderConfigEditorPanelProps["previewStatus"];
  previewData: DirectoryPreviewResponse | undefined;
  previewErrorMessage: string | null;
}) {
  if (isPreviewPending) {
    return (
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-300">
        Checking for matching directories...
      </div>
    );
  }

  if (previewErrorMessage) {
    return (
      <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        {previewErrorMessage}
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border px-4 py-3 text-sm font-medium ${
        isDuplicateDocumentedPath || previewStatus === "already-documented"
          ? "border-emerald-100 bg-emerald-50 text-emerald-700"
          : previewStatus === "has-matches"
          ? "border-blue-100 bg-blue-50 text-blue-300"
          : "border-amber-100 bg-amber-50 text-amber-700"
      }`}
    >
      {isDuplicateDocumentedPath ? (
        <>Already documented — switch to the Documented tab to edit it.</>
      ) : previewStatus === "already-documented" ? (
        <>
          Documented pattern covering{" "}
          <strong>
            {(previewData?.concrete_directory_count ?? 0).toLocaleString()}
          </strong>{" "}
          matching directories across{" "}
          <strong>{(previewData?.cruise_count ?? 0).toLocaleString()}</strong>{" "}
          cruises
        </>
      ) : previewStatus === "has-matches" ? (
        <>
          <strong>
            {(previewData?.concrete_directory_count ?? 0).toLocaleString()}
          </strong>{" "}
          matching directories across{" "}
          <strong>{(previewData?.cruise_count ?? 0).toLocaleString()}</strong>{" "}
          cruises
        </>
      ) : (
        <>
          No matching directories yet — you can still save this for future
          cruises.
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 items-center justify-center p-8 text-center">
      <div>
        <p className="text-lg text-grey-200">Select a directory to document</p>
        <p className="mt-2 max-w-sm text-sm leading-6 text-grey-100">
          Pick one from the list on the left, or click{" "}
          <strong className="text-grey-200">+ New Pattern</strong> to build a
          path from scratch.
        </p>
      </div>
    </div>
  );
}

export default function FolderConfigEditorPanel({
  editingDirectory,
  showForm,
  isDuplicateDocumentedPath,
  previewStatus,
  previewErrorMessage,
  previewData,
  isPreviewPending,
  templatePath,
  previewConcreteMatches,
  selectedPreviewMatch,
  isPreviewExampleOpen,
  onTogglePreviewExampleOpen,
  onShowNextPreviewMatch,
  observedSubdirectories,
  observedFiles,
  previewContentsLoading,
  previewContentsEmpty,
  builder,
  descriptionSeed,
  descriptionEditorResetKey,
  hasDescription,
  onDescriptionChange,
  allowAddendumUpload,
  onAllowAddendumUploadChange,
  selectedDataExpertUserId,
  selectedDataExpertLabel,
  directoryUserOptions,
  directoryDocumentersLoading,
  onSelectedDataExpertChange,
  onStartNew,
  canSubmit,
  saveButtonLabel,
  saveHelpText,
  onSave,
}: FolderConfigEditorPanelProps) {
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white">
      {!showForm ? (
        <EmptyState />
      ) : (
        <>
          {editingDirectory ? (
            <div className="flex shrink-0 items-center justify-between gap-3 rounded-t-xl border-b border-slate-100 bg-blue-50/70 px-5 py-2 text-xs text-blue-300">
              <span>Editing — changes apply to all matching cruises.</span>
              <button
                type="button"
                className="shrink-0 font-semibold underline hover:text-blue-400"
                onClick={onStartNew}
              >
                Start New
              </button>
            </div>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-full flex-col">
              <div className="space-y-4 px-5 py-4">
                {builder}

                {templatePath ? (
                  <div className="space-y-3">
                    <PreviewStatusBanner
                      isDuplicateDocumentedPath={isDuplicateDocumentedPath}
                      isPreviewPending={isPreviewPending}
                      previewStatus={previewStatus}
                      previewData={previewData}
                      previewErrorMessage={previewErrorMessage}
                    />

                    {!isPreviewPending &&
                    !previewErrorMessage &&
                    previewConcreteMatches.length > 0 ? (
                      <PreviewExampleAccordion
                        selectedMatch={selectedPreviewMatch}
                        isOpen={isPreviewExampleOpen}
                        onToggle={onTogglePreviewExampleOpen}
                        matchCount={previewConcreteMatches.length}
                        onShowNextMatch={onShowNextPreviewMatch}
                        observedSubdirectories={observedSubdirectories}
                        observedFiles={observedFiles}
                        isLoading={previewContentsLoading}
                        isEmpty={previewContentsEmpty}
                      />
                    ) : null}
                  </div>
                ) : null}

                <div>
                  <div className="flex items-center gap-1 text-sm font-semibold text-grey-300">
                    <span>Description</span>
                    <span className="text-rose-400">*</span>
                  </div>
                  <div className="mt-1.5">
                    <MarkdownEditor
                      value={descriptionSeed}
                      resetKey={descriptionEditorResetKey}
                      onChange={onDescriptionChange}
                      hasError={Boolean(templatePath) && !hasDescription}
                      placeholder="What kind of data lives here? Any notes for the team?"
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-base font-semibold text-grey-300">
                    Options
                  </p>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div>
                      <p
                        id="directory-data-expert-label"
                        className="block text-sm font-semibold text-grey-300"
                      >
                        Data expert
                      </p>
                      <p
                        id="directory-data-expert-description"
                        className="mt-0.5 text-xs leading-5 text-grey-200"
                      >
                        Assign the in-house expert for the data in this
                        directory.
                      </p>
                      <SearchableSingleSelectDropdown
                        id="directory-data-expert"
                        ariaLabel="Search data experts"
                        labelledBy="directory-data-expert-label"
                        describedBy="directory-data-expert-description"
                        className="mt-2 w-full "
                        buttonClassName="rounded-md"
                        menuClassName="!bottom-full !mb-2 !mt-0 !origin-bottom-left"
                        emptyOptionLabel="No data expert assigned"
                        emptyResultsLabel="No matching teammates"
                        isLoading={directoryDocumentersLoading}
                        options={directoryUserOptions}
                        placeholder="No data expert assigned"
                        searchPlaceholder="Search teammates by email"
                        selectedLabel={selectedDataExpertLabel}
                        value={selectedDataExpertUserId}
                        onChange={onSelectedDataExpertChange}
                      />
                    </div>
                    <div className="mt-3 border-t border-slate-200 pt-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-grey-300">
                            Addendum uploads
                          </p>
                          <p className="mt-0.5 text-xs leading-5 text-grey-200">
                            Allow post-cruise corrections and reports to be
                            uploaded here.
                          </p>
                        </div>
                        <Toggle
                          value={allowAddendumUpload}
                          onChange={onAllowAddendumUploadChange}
                          label="Allow cruise addendum uploads into this directory"
                          className="shrink-0"
                          labelClassName="sr-only"
                          size="small"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-auto flex shrink-0 items-center justify-between gap-4 rounded-b-xl border-t border-slate-200 bg-slate-50 px-5 py-3">
                <p className="text-xs text-grey-200">{saveHelpText}</p>
                <button
                  type="button"
                  className="ox-button !mt-0 w-auto shrink-0 !rounded-[8px] !px-4 !py-1.5 !text-sm"
                  disabled={!canSubmit}
                  onClick={onSave}
                >
                  {saveButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
